import React, { Fragment, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { format } from "date-fns";
import { Modal, Button, Table, Form } from "react-bootstrap";
import BASE_URL from "../../../assets/global/url";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";

// Lists accounts (account_list_sub3) whose Account Balance or Last Balance does
// not tally with their transactions, then on OK rebuilds the running
// account_balance of accountlist_transaction_subject.
const AccountBalanceAudit = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [syncAccountAmount, setSyncAccountAmount] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCheck = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/migration/account-balance-audit`);

      if (res.data.length === 0) {
        swal({
          icon: "success",
          title: "All accounts tally",
          text: "Every Account Balance and Last Balance matches its transactions.",
        });
        return;
      }

      setAccounts(res.data);
      setSelectedIds(res.data.map((account) => account.account_id));
      setExpandedId(null);
      setSyncAccountAmount(false);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      swal({
        icon: "error",
        title: "Failed to check account balances",
        text: error.response?.data?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (accountId) => {
    setSelectedIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(
      selectedIds.length === accounts.length
        ? []
        : accounts.map((account) => account.account_id),
    );
  };

  const handleFix = async () => {
    const confirm = await swal({
      icon: "warning",
      title: "Are you sure?",
      text: `Recalculate Last Balance of ${selectedIds.length} account(s)${
        syncAccountAmount ? " and set their Account Balance to the ledger" : ""
      }?`,
      buttons: ["Cancel", "OK"],
      dangerMode: true,
    });

    if (!confirm) return;

    try {
      setSaving(true);
      const res = await axios.post(
        `${BASE_URL}/migration/account-balance-audit/fix`,
        { accountIds: selectedIds, syncAccountAmount },
      );

      setShowModal(false);
      swal({
        icon: "success",
        title: "Successful",
        text: `Transactions updated: ${res.data.transactionsUpdated}\nAccount Balances updated: ${res.data.accountsAmountUpdated}`,
      });
    } catch (error) {
      console.error(error);
      swal({
        icon: "error",
        title: "Failed to fix account balances",
        text: error.response?.data?.message || "",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (value, currency) =>
    value == null ? "---" : `${currency || ""} ${formatToTwoDecimal(value)}`;

  return (
    <div>
      <span>
        Check accounts where Account Balance / Last Balance does not tally with
        the Journalized Balance{" "}
      </span>
      <button
        className="btn btn-primary"
        onClick={handleCheck}
        disabled={loading}
      >
        {loading ? "Checking..." : "Check Account Balances"}
      </button>

      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            Accounts Not Tally ({accounts.length}) — Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning py-2">
            Nothing has been saved yet. <strong>Ledger Balance</strong> is
            Debit − Credit in the account's own currency ("Funding Capital"
            memo rows excluded). <strong>Journalized</strong> is what View
            Account shows; for USD accounts it is the PHP equivalent. Click a
            row to see the transactions whose Last Balance will change.
          </div>

          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th className="text-center">
                    <Form.Check
                      checked={selectedIds.length === accounts.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>Account</th>
                  <th>Module / Subject</th>
                  <th className="text-end">Account Balance</th>
                  <th className="text-end">Ledger Balance</th>
                  <th className="text-end">Journalized</th>
                  <th className="text-end">Last Balance</th>
                  <th className="text-center">Wrong Rows</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <Fragment key={account.account_id}>
                    <tr
                      className="cursor-pointer"
                      onClick={() =>
                        setExpandedId(
                          expandedId === account.account_id
                            ? null
                            : account.account_id,
                        )
                      }
                    >
                      <td
                        className="text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form.Check
                          checked={selectedIds.includes(account.account_id)}
                          onChange={() => toggleSelected(account.account_id)}
                        />
                      </td>
                      <td>{account.account_name}</td>
                      <td>
                        {account.module_type} / {account.subject_name}
                      </td>
                      <td
                        className={`text-end ${
                          account.amount_mismatch ? "text-danger fw-bold" : ""
                        }`}
                      >
                        {formatAmount(
                          account.account_amount,
                          account.currency_name,
                        )}
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(
                          account.ledger_balance,
                          account.currency_name,
                        )}
                      </td>
                      <td className="text-end">
                        {formatAmount(account.journalized_balance, "PHP")}
                      </td>
                      <td className="text-end">
                        {formatAmount(
                          account.last_balance,
                          account.currency_name,
                        )}
                      </td>
                      <td className="text-center">
                        {account.wrong_rows.length} /{" "}
                        {account.transaction_count}
                      </td>
                    </tr>

                    {expandedId === account.account_id &&
                      account.wrong_rows.length > 0 && (
                        <tr>
                          <td colSpan="8" className="bg-light">
                            <Table bordered size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  <th>Created</th>
                                  <th>Module From</th>
                                  <th>Transaction No.</th>
                                  <th>Remarks</th>
                                  <th>Type</th>
                                  <th className="text-end">Amount</th>
                                  <th className="text-end">
                                    Current Last Balance
                                  </th>
                                  <th className="text-end">
                                    Correct Last Balance
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {account.wrong_rows.map((row) => (
                                  <tr key={row.id}>
                                    <td>
                                      {format(
                                        new Date(row.createdAt),
                                        "MMM dd, yyyy hh:mm:ss a",
                                      )}
                                    </td>
                                    <td>{row.module_from || "---"}</td>
                                    <td>{row.transaction_number || "---"}</td>
                                    <td>{row.remarks || "---"}</td>
                                    <td>{row.type}</td>
                                    <td className="text-end">
                                      {formatToTwoDecimal(row.amount)}
                                    </td>
                                    <td className="text-end text-danger">
                                      {row.current_balance == null
                                        ? "---"
                                        : formatToTwoDecimal(
                                            row.current_balance,
                                          )}
                                    </td>
                                    <td className="text-end text-success fw-bold">
                                      {formatToTwoDecimal(row.correct_balance)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>

          <Form.Check
            className="mt-2"
            id="sync-account-amount"
            label="Also set Account Balance to the Ledger Balance (only for accounts in red)"
            checked={syncAccountAmount}
            onChange={(e) => setSyncAccountAmount(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleFix}
            disabled={saving || selectedIds.length === 0}
          >
            {saving ? "Saving..." : `OK — Fix ${selectedIds.length} Account(s)`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AccountBalanceAudit;
